<template>
  <section v-if="mappedOrderlines.length" class="py-24">
    <div class="mx-auto max-w-full px-24 md:max-w-[900px] md:px-32">
      <h2 class="heading-2 mb-8">{{ i18n(translations.title) }}</h2>

      <div class="mb-16 flex gap-8">
        <RevChip
          v-for="filter in ITEM_FILTERS"
          :key="filter.value"
          :label="i18n(filter.label)"
          :model-value="selectedFilter === filter.value"
          @click="selectedFilter = filter.value"
        />
      </div>

      <div class="flex gap-16 overflow-x-auto pb-4">
        <HelpCenterItemCard
          v-for="orderline in filteredOrderlines"
          :key="orderline.key"
          class="w-[233px] shrink-0"
          v-bind="orderline.cardProps"
        />
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { useAsyncData } from '#imports'
import { computed, ref } from 'vue'

import { useBuybackEnabled } from '@backmarket/nuxt-layer-buyback/app/composables/useBuybackEnabled/useBuybackEnabled'
import {
  type FormattedStatusTimeline,
  formatStatusTimeline,
} from '@backmarket/nuxt-layer-buyback/app/utils/adapters'
import { useUserStore } from '@backmarket/nuxt-layer-oauth/useUserStore'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevChip } from '@ds/components/Chip'
import type { Variants as TagVariant } from '@ds/components/Tag'

import { API_TAG_LABEL_CLASS_TO_TAG_VARIANT } from '@/scopes/care-commons/components/order/OrderlineCard/OrderlineCard.constants'
import type { Orderline } from '~/api/my-orders/types/OrderlineList.types'
import { ORDERLINE_TIMELINE_STEP_TYPE } from '~/api/my-orders/types/OrderlineTimeline.types'
import { useRouteLocationWithLocale } from '~/composables/useRouteLocationWithLocale'
import { ROUTE_NAMES } from '~/scopes/dashboard/constants'

import HelpCenterItemCard from '../HelpCenterItemCard/HelpCenterItemCard.vue'

import translations from './OrdersSection.translations'

const i18n = useI18n()
const $httpFetch = useHttpFetch()
const { isUserAuthenticated } = useUserStore()
const { isRegularTradeInEnabled } = useBuybackEnabled()
const resolveLocalizedRoute = useRouteLocationWithLocale()

type CardType = 'sale' | 'trade-in'
type ItemFilter = 'all' | 'purchases' | 'trade-ins'

const FILTER_TO_CARD_TYPE: Record<Exclude<ItemFilter, 'all'>, CardType> = {
  purchases: 'sale',
  'trade-ins': 'trade-in',
}

const ITEM_FILTERS: Array<{
  value: ItemFilter
  label: (typeof translations)[keyof typeof translations]
}> = [
  { value: 'all', label: translations.filterAll },
  { value: 'purchases', label: translations.filterPurchases },
  { value: 'trade-ins', label: translations.filterTradeIns },
]

const selectedFilter = ref<ItemFilter>('all')

const { data: orderlines } = useAsyncData(
  () =>
    $httpFetch('/my-orders/orderlines', {
      query: { pageSize: 10 },
    }),
  { immediate: isUserAuthenticated.value },
)

const { data: tradeIns } = useAsyncData(
  async () =>
    await $httpFetch('/bm/buyback/v1/customer/orders', {
      query: { page: 1 },
    }),
  { immediate: isUserAuthenticated.value && isRegularTradeInEnabled },
)

const ordersPageTo = resolveLocalizedRoute({
  type: 'internal',
  name: ROUTE_NAMES.DASHBOARD.MY_ORDERS.SELF,
  params: {},
  query: {},
})

const tradeInsPageTo = resolveLocalizedRoute({
  type: 'internal',
  name: ROUTE_NAMES.DASHBOARD.MY_SALES,
  params: {},
  query: {},
})

// state.labelClass is 'DEFAULT' | 'SUCCESS' | 'DANGER' | 'WARNING' | null
// (no 'INFO' — that only appears on tag.labelClass)
type StateLabelClass = Orderline['state']['labelClass']

function toTagVariant(labelClass: StateLabelClass): TagVariant {
  return (API_TAG_LABEL_CLASS_TO_TAG_VARIANT[labelClass ?? 'DEFAULT'] ??
    'secondary') as TagVariant
}

const SHORT_DATE_FORMAT: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
}

const WEEKDAY_DATE_FORMAT: Intl.DateTimeFormatOptions = {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
}

function getDateLabel(orderline: Orderline): string | undefined {
  const { enumState, date } = orderline.state

  // PENDING_PAYMENT: "Payment due by: Friday, Jul. 31"
  if (enumState === 'PENDING_PAYMENT') {
    if (!date) return undefined

    return i18n(translations.datePrefixPaymentDueBy, {
      date: i18n.date(new Date(date), WEEKDAY_DATE_FORMAT),
    })
  }

  // PAID / VALIDATED / SHIPPED: "Estimated delivery: Jul. 27-29"
  // Source: the delivery timeline step's dateRange (or single date as fallback)
  if (
    enumState === 'PAID' ||
    enumState === 'VALIDATED' ||
    enumState === 'SHIPPED'
  ) {
    const deliveryStep = orderline.timeline?.find(
      (s) => s.type === ORDERLINE_TIMELINE_STEP_TYPE.delivery,
    )

    if (deliveryStep?.dateRange) {
      const min = i18n.date(
        new Date(deliveryStep.dateRange.minDate),
        SHORT_DATE_FORMAT,
      )
      const max = i18n.date(new Date(deliveryStep.dateRange.maxDate), {
        day: 'numeric',
      })

      return i18n(translations.datePrefixEstimatedDelivery, {
        date: `${min}-${max}`,
      })
    }

    if (deliveryStep?.date) {
      return i18n(translations.datePrefixEstimatedDelivery, {
        date: i18n.date(new Date(deliveryStep.date), SHORT_DATE_FORMAT),
      })
    }

    return undefined
  }

  // All other statuses with a point-in-time date: "On: Friday, Jul. 31"
  // Covers: DELIVERED, CANCELLED, REFUNDED, NOT_PAID, AWAITING_CONFIRMATION,
  //         REPLACED, EXPRESS_REPLACE_COMPLETE, EXPRESS_REPLACE_ONGOING
  if (date) {
    return i18n(translations.datePrefixOn, {
      date: i18n.date(new Date(date), WEEKDAY_DATE_FORMAT),
    })
  }

  return undefined
}

function getTradeInDateLabel(
  currentStep: FormattedStatusTimeline,
): string | undefined {
  return currentStep.date
    ? i18n(translations.datePrefixOn, {
        date: i18n.date(currentStep.date, WEEKDAY_DATE_FORMAT),
      })
    : undefined
}

function compareByDateDesc(
  a: { sortDate: Date | null },
  b: { sortDate: Date | null },
): number {
  const aTime = a.sortDate?.getTime() ?? Number.NEGATIVE_INFINITY
  const bTime = b.sortDate?.getTime() ?? Number.NEGATIVE_INFINITY

  return bTime - aTime
}

const salesCards = computed(() => {
  if (!orderlines.value?.results?.length) return []

  return orderlines.value.results
    .filter(
      // Only show orderlines where the state has a human-readable label.
      // state.label is null for UNKNOWN / unmapped statuses — skip those.
      (orderline): orderline is Orderline & { state: { label: string } } =>
        orderline.state.label !== null,
    )
    .map((orderline) => ({
      key: `sale-${orderline.publicId}`,
      type: 'sale' as CardType,
      sortDate: new Date(orderline.creationDate),
      cardProps: {
        tagLabel: orderline.state.label,
        tagVariant: toTagVariant(orderline.state.labelClass),
        dateLabel: getDateLabel(orderline),
        imageUrl: orderline.product.image,
        imageAlt: orderline.product.title,
        title: orderline.product.title,
        subtitle: orderline.tag?.label ?? undefined,
        to: ordersPageTo,
      },
    }))
})

const tradeInCards = computed(() => {
  if (!tradeIns.value?.results?.length) return []

  return tradeIns.value.results
    .filter(
      // Only show trade-ins with a status timeline — it carries the status tag
      // and the dates shown on the card.
      (sale) => sale.order.statusTimeline.length > 0,
    )
    .map((sale) => {
      const steps = formatStatusTimeline(sale.order.statusTimeline)
      const currentStep = steps.find((step) => step.current) ?? steps[0]

      return {
        key: `trade-in-${sale.order.id}`,
        type: 'trade-in' as CardType,
        // The first timeline step date is the order creation date.
        sortDate: steps[0]?.date ?? null,
        cardProps: {
          tagLabel: currentStep.tag,
          tagVariant: 'alternate' as TagVariant,
          dateLabel: getTradeInDateLabel(currentStep),
          imageUrl: sale.product.image.light,
          imageAlt: sale.product.title,
          title: sale.product.title,
          to: tradeInsPageTo,
        },
      }
    })
})

const mappedOrderlines = computed(() =>
  [...salesCards.value, ...tradeInCards.value].sort(compareByDateDesc),
)

const filteredOrderlines = computed(() => {
  if (selectedFilter.value === 'all') return mappedOrderlines.value

  const cardType = FILTER_TO_CARD_TYPE[selectedFilter.value]

  return mappedOrderlines.value.filter((card) => card.type === cardType)
})
</script>
